import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { SlutopgoerelseAfregnRequestDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/slutopgoerelse-afregn-request-dto';
import { SlutopgoerelseBaseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/slutopgoerelse-base-dto';
import { SlutopgoerelseUpdateDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/slutopgoerelse-update-dto';
import { KundesegmentEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/kundesegment-enum';
import { GetServiceaftaleResponse } from 'src/app/api/models/OK/Egis/Service/Models/Response/get-serviceaftale-response';
import { ServiceAftaleService, SlutopgoerelseService } from 'src/app/api/services';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';

@Component({
    selector: 'app-slutopgoerelse-editor',
    templateUrl: './slutopgoerelse-editor.component.html',
    styleUrls: ['./slutopgoerelse-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SlutopgoerelseEditorComponent implements OnInit {
  constructor(
    private formBuilder: UntypedFormBuilder,
    private errorHandler: ErrorHandlerService,
    private loader: LoaderService,
    private serviceaftaleService: ServiceAftaleService,
    private slutopgoerelseService: SlutopgoerelseService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  get slutopgoerelseRemark() {
    return this.slutopgoerelseForm.get('slutopgoerelseRemark');
  }

  get payUntilEndOfPeriod() {
    return this.slutopgoerelseForm.get('payUntilEndOfPeriod');
  }

  get manuelCorrection() {
    return this.slutopgoerelseForm.get('manuelCorrection');
  }

  get tilvalgCorrection() {
    return this.slutopgoerelseForm.get('tilvalgCorrection');
  }

  slutopgoerelseForm: UntypedFormGroup;
  okKundeAftaleNr: number;
  serviceaftale: ServiceaftaleDto;
  unsubscribe$ = new Subject<void>();
  isSlutopgPastDueDate: boolean = false;
  isEdit: boolean = false;
  endDate: Date = new Date();
  endDateTodayOrBefore: boolean;

  ngOnInit(): void {
    const okKundeAftaleNr = Number(this.route.snapshot.paramMap.get('okKundeAftaleNr'));
    this.okKundeAftaleNr = okKundeAftaleNr;

    const isEditFromRoute = this.route.snapshot.data.edit;
    this.isEdit = isEditFromRoute != null && isEditFromRoute;

    this.loader.show();

    this.serviceaftaleService
      .getServiceAftaleForKunde({
        okKundeAftaleNr: okKundeAftaleNr,
        caller: EGIS_SERVICE_CALLER,
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (response: GetServiceaftaleResponse) => {
          let isPrivat = response.serviceaftale.kunde?.kundetype?.kundeSegment == KundesegmentEnum.Privat;
          this.slutopgoerelseForm = this.formBuilder.group({
            createSlutopgoerelse: [true, Validators.required],
            slutopgoerelseRemark: [response.serviceaftale.slutopgoerelse?.remark],
            payUntilEndOfPeriod: [response.serviceaftale.slutopgoerelse?.payUntilEndOfPeriod],
            manuelCorrection: [
              isPrivat
                ? response.serviceaftale.slutopgoerelse?.manuelCorrectionWithMoms
                : response.serviceaftale.slutopgoerelse?.manuelCorrectionWithoutMoms,
              [Validators.min(-5000), Validators.max(5000)],
            ],
            addManuelCorrection: [
              response.serviceaftale.slutopgoerelse?.manuelCorrectionWithMoms != null ||
                response.serviceaftale.slutopgoerelse?.manuelCorrectionWithoutMoms != null,
            ],
            tilvalgCorrection: [response.serviceaftale.slutopgoerelse?.correctForFrieTilkald],
          });
          this.serviceaftale = response.serviceaftale;
          this.endDate = new Date(this.serviceaftale.endDate);
          this.endDateTodayOrBefore = this.endDate <= new Date();
          this.isSlutopgPastDueDate = new Date(response.serviceaftale.slutopgoerelse.effectiveDate) < new Date();
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  AfregnSlutopgoerelse() {
    let slutopgoerelseBase: SlutopgoerelseBaseDto = {
      slutopgoerelseRemark: this.slutopgoerelseRemark.value,
      payUntilEndOfPeriod: this.payUntilEndOfPeriod.value,
      manuelCorrectionAmount: this.slutopgoerelseForm.controls.addManuelCorrection.value ? this.manuelCorrection.value : null,
      correctForFrieTilkald: this.tilvalgCorrection.value,
    };

    let update: SlutopgoerelseUpdateDto = {
      id: this.serviceaftale.slutopgoerelse.id,
      slutopgoerelse: slutopgoerelseBase,
    };

    let afregn: SlutopgoerelseAfregnRequestDto = {
      slutopgoerelseId: this.serviceaftale.slutopgoerelse.id,
      slutopgoerelseUpdate: this.slutopgoerelseForm.dirty ? update : null,
    };

    this.loader.show();
    this.slutopgoerelseService
      .afregnSlutopgoerelse({ caller: EGIS_SERVICE_CALLER, body: afregn })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        () => {
          this.goBack();
        },
        (error) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  UpdateSlutopgoerelse() {
    this.loader.show();
    let slutopgoerelseBase: SlutopgoerelseBaseDto = {
      slutopgoerelseRemark: this.slutopgoerelseRemark.value,
      payUntilEndOfPeriod: this.payUntilEndOfPeriod.value,
      manuelCorrectionAmount: this.slutopgoerelseForm.controls.addManuelCorrection.value ? this.manuelCorrection.value : null,
      correctForFrieTilkald: this.tilvalgCorrection.value,
    };
    let update: SlutopgoerelseUpdateDto = {
      id: this.serviceaftale.slutopgoerelse.id,
      slutopgoerelse: slutopgoerelseBase,
    };
    this.slutopgoerelseService
      .updateSlutopgoerelse({ caller: EGIS_SERVICE_CALLER, body: update })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        this.goBack();
      }),
      (error) => {
        this.errorHandler.HandleError(error);
      };
  }

  onCloseClick() {
    if (!this.slutopgoerelseForm.dirty) {
      this.goBack();
    } else {
      let dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Er du sikker?',
          body: 'Er du sikker på at du vil afbryde?',
        },
      });

      dialogRef.afterClosed().subscribe((confirm) => {
        if (confirm) {
          this.goBack();
        }
      });
    }
  }

  momsTekst() {
    const moms = this.serviceaftale?.kunde?.kundetype?.kundeSegment === KundesegmentEnum.Privat ? 'inkl. moms' : 'ekskl. moms';
    return `Alle beløb er ${moms}`;
  }

  private goBack() {
    window.history.back();
  }
}
